import { getCSRFToken } from '../utils/getCSRFToken';
import { mountStripePaymentElement } from '../utils/mountStripePaymentElement';

export default function registerStorePaymentStore() {
  Alpine.store('storePaymentStore', {
    // Payment data
    stripe: null,
    elements: null,
    paymentElement: null,
    clientSecret: null,
    stripePublishableKey: null,
    stripeAccountId: null,

    // UI state
    isProcessing: false,
    errorMessage: '',
    successMessage: '',

    // Stripe appearance config
    stripeAppearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#000000',
      },
    },

    initialize(stripePublishableKey, stripeAccountId, clientSecret) {
      console.log('Payment store initialize called with:', {
        stripePublishableKey,
        stripeAccountId,
        clientSecret,
      });

      this.stripePublishableKey = stripePublishableKey;
      this.stripeAccountId = stripeAccountId;
      this.clientSecret = clientSecret;

      // First, ensure Stripe script is loaded
      if (typeof Stripe === 'undefined') {
        console.log('Loading Stripe script...');
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.onload = () => {
          console.log('Stripe script loaded');
          this.initializeWhenReady();
        };
        document.head.appendChild(script);
      } else {
        this.initializeWhenReady();
      }
    },

    initializeWhenReady() {
      // Check immediately first
      const paymentContainer = document.getElementById('payment-element');
      console.log('Initial check:', {
        paymentContainer: !!paymentContainer,
        stripeAvailable: typeof Stripe !== 'undefined',
        containerHTML: paymentContainer?.innerHTML,
      });

      if (paymentContainer && typeof Stripe !== 'undefined') {
        console.log('Payment container found immediately, initializing Stripe');
        this.initializeStripe();
        this.initializePaymentElement();
        return;
      }

      // If not found, set up observer for later
      console.log('Setting up mutation observer...');
      const observer = new MutationObserver((mutations, obs) => {
        const paymentContainer = document.getElementById('payment-element');
        console.log('Observer check:', {
          paymentContainer: !!paymentContainer,
          stripeAvailable: typeof Stripe !== 'undefined',
        });

        if (paymentContainer && typeof Stripe !== 'undefined') {
          console.log(
            'Payment container found through observer, initializing Stripe',
          );
          this.initializeStripe();
          this.initializePaymentElement();
          obs.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      console.log('Observer set up complete');
    },

    initializeStripe() {
      console.log(
        'initializeStripe called, Stripe available:',
        typeof Stripe !== 'undefined',
      );
      console.log('Current stripe instance:', this.stripe);

      if (!this.stripe && typeof Stripe !== 'undefined') {
        console.log('Creating new Stripe instance with:', {
          key: this.stripePublishableKey,
          account: this.stripeAccountId,
        });

        // Initialize Stripe with the connected account
        this.stripe = Stripe(this.stripePublishableKey, {
          stripeAccount: this.stripeAccountId,
        });

        console.log('Stripe instance created:', !!this.stripe);
      }
    },

    initializePaymentElement(reinitialize = false) {
      console.log(
        'initializePaymentElement called with reinitialize:',
        reinitialize,
      );
      console.log('Current state:', {
        stripe: !!this.stripe,
        elements: !!this.elements,
        paymentElement: !!this.paymentElement,
        clientSecret: !!this.clientSecret,
      });

      if (!this.stripe) {
        console.log('No Stripe instance, initializing...');
        this.initializeStripe();
      }

      // If reinitializing or first time setup
      if (
        (reinitialize || !this.elements) &&
        this.stripe &&
        this.clientSecret
      ) {
        console.log('Setting up Elements...');

        // Clear existing elements if reinitializing
        if (reinitialize && this.paymentElement) {
          console.log('Destroying existing payment element');
          this.paymentElement.destroy();
        }

        // Initialize Elements
        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          appearance: this.stripeAppearance,
        });
        console.log('Elements created:', !!this.elements);

        // Create new Payment Element
        this.paymentElement = this.elements.create('payment', {
          layout: 'tabs',
        });
        console.log('Payment element created:', !!this.paymentElement);
      }

      // Mount the payment element
      setTimeout(() => {
        const paymentElement = document.getElementById('payment-element');
        console.log('Payment container found:', !!paymentElement);

        if (paymentElement) {
          mountStripePaymentElement(this.paymentElement, '#payment-element');
          console.log('Payment element mounted');
        } else {
          console.error('Payment element container not found');
        }
      }, 100);
    },

    async processPayment() {
      if (!this.stripe || !this.paymentElement) {
        this.errorMessage = 'Payment system not initialized properly';
        return;
      }

      this.isProcessing = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const { error } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams: {
            return_url: window.location.href + 'success/',
          },
        });

        if (error) {
          this.errorMessage = error.message;
        }
        // If successful, the return_url will handle the redirect
      } catch (err) {
        console.error('Payment failed:', err);
        this.errorMessage = 'An unexpected error occurred. Please try again.';
      } finally {
        this.isProcessing = false;
      }
    },
  });
}
